import cx from 'classnames'
import { SortBy } from 'components/artworks/SortBy'
import { Button } from 'components/common/Button'
import { Icon } from 'components/common/Icon'
import { Input } from 'components/common/Input'
import { debounce } from 'debounce'
import { useCurrentNumberOfFilters } from 'hooks/artworks/useCurrentNumberOfFilters'
import useTranslation from 'next-translate/useTranslation'
import { useRouter } from 'next/router'
import { useEffect, useRef } from 'react'
import { useClearRefinements, useSearchBox } from 'react-instantsearch'

type CommonProps = {
  scrollToArtworks: () => void
}

type HeaderProps = {
  showFilters: boolean
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>
  setShowMobileFilters: React.Dispatch<React.SetStateAction<boolean>>
} & CommonProps

const SearchBox = ({ scrollToArtworks }: CommonProps) => {
  const { t } = useTranslation()
  const router = useRouter()
  const { query } = router
  const { refine: refineSearch } = useSearchBox()

  const debounceScrollToArtworks = useRef(
    debounce(() => {
      scrollToArtworks()
    }, 500)
  )

  useEffect(() => {
    if (!query.q) return

    refineSearch(String(query.q))
  }, [query, refineSearch])

  return (
    <Input
      type='algolia-search'
      placeholder={t('common:cta_search')}
      size='lg'
      className='w-full'
      onKeyDown={debounceScrollToArtworks.current}
    />
  )
}

export const Header = ({
  scrollToArtworks,
  setShowFilters,
  setShowMobileFilters,
  showFilters,
}: HeaderProps) => {
  const { t } = useTranslation('artworks')

  const { refine: clearFilters } = useClearRefinements()
  const currentNumberOfFilters = useCurrentNumberOfFilters()

  return (
    <div className='container flex items-center justify-end space-x-4'>
      <SearchBox scrollToArtworks={scrollToArtworks} />

      <div className='block lg:hidden'>
        <Button
          onClick={() => {
            setShowMobileFilters(true)
          }}
          style='outline'
          size='lg'
          className={cx(
            'relative w-12 items-center justify-center space-x-4 sm:flex sm:w-fit lg:hidden',
            {
              '!border-cool-800': currentNumberOfFilters > 0,
            }
          )}
        >
          <Icon name='configure' size={24} className='text-cool-700' />
          <span className='hidden sm:block'>
            {' '}
            {t('artworks:filters').charAt(0).toUpperCase() +
              t('artworks:filters').slice(1).toLowerCase()}
          </span>
          {currentNumberOfFilters > 0 && (
            <span className='absolute -right-3 -top-3 flex h-6 w-6 items-center justify-center rounded-full bg-cool-800 text-2xs text-cool-50'>
              {currentNumberOfFilters}
            </span>
          )}
        </Button>
      </div>

      <div className='z-10 hidden flex-row space-x-4 lg:flex'>
        {currentNumberOfFilters > 0 && (
          <Button
            className='!h-12 whitespace-nowrap'
            onClick={() => {
              clearFilters()
              scrollToArtworks()
            }}
          >
            <Icon name='close' size={16} className='mr-2' />
            {t('clear_filters')} ({currentNumberOfFilters})
          </Button>
        )}

        <Button
          onClick={() => setShowFilters(!showFilters)}
          style='outline'
          size='md'
          className='!h-12 whitespace-nowrap'
        >
          <Icon
            name={showFilters ? 'hide' : 'view'}
            size={16}
            className='mr-2'
          />{' '}
          {showFilters ? t('artworks:hide') : t('artworks:view')}{' '}
          {t('artworks:filters')}
        </Button>

        <SortBy
          before={`${t('sort_by')} `}
          className='!h-12 w-fit min-w-[220px]'
        />
      </div>
    </div>
  )
}
